import cx from 'classnames';
import { useContext } from 'react';
import { PrimaryButton } from '@/common/Button';
import { FinanceStatus } from '@/common/FinanceCard/constants';
import StatusIndicator from '@/common/StatusIndicator';
import SoftPullStatus from '~/constants/softPullStatus';
import ModalContext from '~/context/vdp/ModalContext';
import UserVehicleContext from '~/context/vdp/UserVehicleContext';
import useSoftPullStatus from '~/hooks/vdp/selectors/useSoftPullStatus';
import TermsErrorIcon from '~/icons/TermsErrorIcon';
import { PricingError } from '~/services/srp/contracts/PricingResponse';
import {
  ButtonWrapper,
  Contents,
  Title,
  Wrapper,
  CardHeader
} from '../WithoutSoftpull/Card.styles';
import CommonCard from '../WithoutSoftpull/CommonCard';
import NonSoftPullPayOnceCard from '../WithoutSoftpull/PayOnceCard';
import PayOnceCard from '../WithSoftpull/PayOnceCard';

export default function SptErrorState() {
  const { vdpPricingError } = useContext(UserVehicleContext);
  const { softPullStatus } = useSoftPullStatus();
  const { setSoftPullModalOpen } = useContext(ModalContext);

  const handleCTAClick = () => {
    if (vdpPricingError === PricingError.NoVehiclePricingInfo) {
      setSoftPullModalOpen(true);
    } else {
      window.location.reload();
    }
  };

  const hasSoftPull = softPullStatus === SoftPullStatus.Active;
  const hasExpiredFinancing = softPullStatus === SoftPullStatus.Expired;
  const showNonSoftPullPayOnceCard =
    !hasSoftPull && vdpPricingError === PricingError.NoVehiclePricingInfo;
  const showSoftPullPayOnceCard =
    hasSoftPull && vdpPricingError === PricingError.NoVehiclePricingInfo;

  return (
    <Wrapper className={!showNonSoftPullPayOnceCard ? 'm:w-[680px]' : ''}>
      {showSoftPullPayOnceCard ? (
        <CommonCard isLoading={false} isLongCard={true}>
          <CardHeader>
            <div className="flex justify-between">
              <p className="text-blue-6 tl-header-m">Your Financing</p>
              <StatusIndicator
                status={FinanceStatus.SoftPull}
                text="Pre-Qualified"
              />
            </div>
            <p className="tl-body-l">
              An error occurred when loading finance terms.{' '}
              <a className="text-blue-2 tl-link-m hover:text-blue-3">
                Try refreshing the page
              </a>
            </p>
          </CardHeader>
          <PayOnceCard />
        </CommonCard>
      ) : (
        <>
          {showNonSoftPullPayOnceCard && <NonSoftPullPayOnceCard />}
          {hasExpiredFinancing ? null : (
            <CommonCard
              isLoading={false}
              isLongCard={!showNonSoftPullPayOnceCard}
            >
              {showNonSoftPullPayOnceCard && <Title>Pay Monthly</Title>}
              <Contents>
                <TermsErrorIcon className="mx-auto mb-16" />
                <p className="text-center text-blue-6 tl-header-m">
                  An error occurred when loading{' '}
                  {vdpPricingError === PricingError.NoVehiclePricingInfo
                    ? 'monthly payment'
                    : 'price details'}
                </p>
                <p
                  className={cx('mt-8 text-center text-blue-6 tl-body-m', {
                    hidden:
                      vdpPricingError !== PricingError.NoVehiclePricingInfo
                  })}
                >
                  Get pre-qualified to see real, personalized finance terms.
                </p>
                <ButtonWrapper className="mt-32">
                  <PrimaryButton
                    className="w-full"
                    onClick={handleCTAClick}
                    data-analytics-id="Price Details - Pay Once - Get Started"
                    size="medium"
                  >
                    {vdpPricingError === PricingError.NoVehiclePricingInfo
                      ? 'Get Your Terms'
                      : 'Refresh Page'}
                  </PrimaryButton>
                </ButtonWrapper>
              </Contents>
            </CommonCard>
          )}
        </>
      )}
    </Wrapper>
  );
}
